import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authFetch, readJSON } from '../lib/auth';
import { payWithRazorpay, loadRazorpayCheckout } from '../lib/razorpay';
import { useAuth } from '../contexts/AuthContext';
import { Crown, Check, Sparkles, ArrowLeft, Loader2 } from 'lucide-react';
import { SiteFooter } from '../components/LegalLayout';
import BrandLogo from '../components/BrandLogo';

const FEATURES = {
  starter_monthly: [
    '10 exports per month',
    'No watermark on exports',
    'Presentation Mode + HD export',
    'Perfect for occasional users',
  ],
  pro_monthly: [
    '20 exports per month',
    'AI Finalize (Gemini Nano Banana)',
    'No watermark on exports',
    'Presentation Mode + HD export',
    'Priority support',
  ],
  pro_yearly: [
    'Everything in Pro Monthly',
    '~17% discount vs monthly',
    'Lock in current pricing',
  ],
  credits_25: [
    '25 AI Finalize calls',
    'Never expire',
    'Stack with Pro for extra calls',
  ],
  credits_80: [
    '80 AI Finalize calls — best value',
    'Never expire',
    'Stack with Pro for extra calls',
  ],
};

function PriceCard({ pkg, inrPkg, highlight, features, onPick, onPickInr, busy, busyInr }) {
  const isCredits = pkg.type === 'credits';
  return (
    <div
      data-testid={`pricing-card-${pkg.id}`}
      className={`relative bg-slate-800/60 backdrop-blur-sm border ${
        highlight
          ? 'border-amber-400/60 shadow-[0_0_30px_rgba(255,180,80,0.2)]'
          : 'border-slate-700/60'
      } rounded-2xl p-6 flex flex-col`}
    >
      {highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg uppercase tracking-wider">
          Most Popular
        </div>
      )}
      <div className="flex items-center gap-2 mb-1">
        {isCredits ? (
          <Sparkles className="w-5 h-5 text-emerald-400" />
        ) : (
          <Crown className="w-5 h-5 text-violet-300" />
        )}
        <h3 className="text-lg font-bold text-white">{pkg.name}</h3>
      </div>
      <p className="text-xs text-slate-400 mb-4">{pkg.description}</p>
      <div className="mb-4 space-y-1">
        <div>
          <span className="text-4xl font-extrabold text-white">${pkg.amount.toFixed(2)}</span>
          <span className="text-sm text-slate-400 ml-1">
            {pkg.id === 'pro_monthly' && '/month'}
            {pkg.id === 'pro_yearly' && '/year'}
            {isCredits && ' one-time'}
          </span>
        </div>
        {inrPkg && (
          <div className="text-xs text-slate-400">
            or <span className="text-emerald-300 font-semibold">₹{inrPkg.amount_inr}</span> via UPI / Indian cards
          </div>
        )}
      </div>
      <ul className="space-y-2 mb-6 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
            <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-400" />
            {f}
          </li>
        ))}
      </ul>
      <div className="flex flex-col gap-2">
        <button
          onClick={() => onPick(pkg.id)}
          disabled={busy || busyInr}
          data-testid={`pricing-buy-${pkg.id}`}
          className={`w-full py-2.5 rounded-lg font-semibold transition shadow ${
            highlight
              ? 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white'
              : 'bg-slate-700 hover:bg-slate-600 text-white'
          } disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {busy ? 'Opening Stripe…' : (isCredits ? 'Buy with Card (USD)' : 'Subscribe with Card (USD)')}
        </button>
        {inrPkg && (
          <>
            <div
              data-testid={`pricing-india-badge-${pkg.id}`}
              className="flex items-center justify-center gap-1.5 -mb-1 mt-1 text-[11px] text-emerald-300/90"
            >
              <span className="inline-block w-1 h-1 rounded-full bg-emerald-400" />
              Made in India 🇮🇳 — Pay in INR
              <span className="inline-block w-1 h-1 rounded-full bg-emerald-400" />
            </div>
            <button
              onClick={() => onPickInr(pkg.id)}
              disabled={busy || busyInr}
              data-testid={`pricing-buy-inr-${pkg.id}`}
              className="w-full py-2.5 rounded-lg font-semibold transition shadow bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {busyInr ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {busyInr ? 'Opening Razorpay…' : `Pay ₹${inrPkg.amount_inr} (UPI / Indian Cards)`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  const { signedIn, user, isPro } = useAuth();
  const [packages, setPackages] = useState([]);
  const [inrPackages, setInrPackages] = useState([]);
  const [busy, setBusy] = useState('');
  const [busyInr, setBusyInr] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    authFetch('/api/billing/packages')
      .then(readJSON)
      .then((d) => setPackages(d.packages || []))
      .catch(() => setError('Could not load pricing.'));
    // INR packages from Razorpay route — separate so failure of one doesn't break the page
    authFetch('/api/billing/razorpay/packages')
      .then(readJSON)
      .then((d) => setInrPackages(d.packages || []))
      .catch(() => {});
    // Warm up the Razorpay script
    loadRazorpayCheckout();
  }, []);

  const startCheckout = async (packageId) => {
    setError('');
    if (!signedIn) {
      navigate('/login');
      return;
    }
    setBusy(packageId);
    try {
      const res = await authFetch('/api/billing/checkout', {
        method: 'POST',
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin,
        }),
      });
      const data = await readJSON(res);
      if (!res.ok) {
        throw new Error(data?.detail || data?.message || 'Could not start checkout');
      }
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const startRazorpay = async (packageId) => {
    setError('');
    if (!signedIn) {
      navigate('/login');
      return;
    }
    setBusyInr(packageId);
    try {
      const result = await payWithRazorpay({
        packageId,
        user,
        onSuccessRedirect: `${window.location.origin}/billing/success`,
      });
      // payWithRazorpay redirects on success — fallback if it didn't:
      if (result?.granted) {
        navigate('/billing/success?source=razorpay&granted=1');
      }
    } catch (e) {
      const msg = e?.message || '';
      // "Payment cancelled" is a normal user action, not an error to scream at.
      // Also filter out the harmless analytics-interceptor "clone" race that
      // can occur while Razorpay's iframe is loading. The modal still opens
      // and payment still works in that case.
      const isCloneNoise =
        msg.includes('Response body is already used') ||
        (msg.includes('clone') && msg.includes('Response'));
      if (msg !== 'Payment cancelled' && !isCloneNoise) {
        setError(msg || 'Payment failed');
      }
    } finally {
      setBusyInr('');
    }
  };

  const subs = packages.filter((p) => p.type === 'subscription_like');
  const credits = packages.filter((p) => p.type === 'credits');
  const inrById = Object.fromEntries(inrPackages.map((p) => [p.id, p]));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="text-slate-400 hover:text-amber-400 flex items-center gap-1 text-sm" data-testid="pricing-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to studio
          </Link>
          <div className="flex items-center">
            <BrandLogo className="w-28" />
          </div>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">
            Light up your designs with <span className="text-amber-400">Pro</span>
          </h1>
          <p className="text-slate-400 mt-3 max-w-2xl mx-auto">
            Unlock AI Finalize, unlimited projects, and watermark-free exports.
            Cancel any time — pricing locked at signup.
          </p>
          {isPro && (
            <div className="mt-4 inline-block bg-violet-500/20 border border-violet-500/30 text-violet-200 text-sm px-3 py-1 rounded-full">
              ✓ You're already Pro {user?.pro_until && <span className="opacity-70">(until {String(user.pro_until).slice(0, 10)})</span>}
            </div>
          )}
        </div>

        {error && (
          <div className="mb-6 text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg px-4 py-3" data-testid="pricing-error">
            {error}
          </div>
        )}

        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-3">Subscription Plans</h2>
        <div className={`grid grid-cols-1 ${subs.length >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4 mb-12`}>
          {subs.map((pkg) => (
            <PriceCard
              key={pkg.id}
              pkg={pkg}
              inrPkg={inrById[pkg.id]}
              highlight={pkg.id === 'pro_monthly'}
              features={FEATURES[pkg.id] || []}
              onPick={startCheckout}
              onPickInr={startRazorpay}
              busy={busy === pkg.id}
              busyInr={busyInr === pkg.id}
            />
          ))}
        </div>

        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-3">AI Credit Packs</h2>
        <p className="text-xs text-slate-500 mb-3">
          Don't want a subscription? Buy credits for individual AI Finalize calls. Credits never expire and stack with Pro.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {credits.map((pkg) => (
            <PriceCard
              key={pkg.id}
              pkg={pkg}
              inrPkg={inrById[pkg.id]}
              highlight={pkg.id === 'credits_80'}
              features={FEATURES[pkg.id] || []}
              onPick={startCheckout}
              onPickInr={startRazorpay}
              busy={busy === pkg.id}
              busyInr={busyInr === pkg.id}
            />
          ))}
        </div>

        <div className="text-center text-xs text-slate-500 mt-12 space-y-1">
          <p>International cards via Stripe. UPI, Indian cards, netbanking & wallets via Razorpay.</p>
          <p>We never see or store your card details.</p>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
